// Record money — PROFIT SHARE (17 Sept 2026).
//
// Operator, on Record money after paying Loki OTC 50,000 INR of cash from the
// angadiya (PAY-2026-09-16-HNL4L): "this is Loki's profit drawing, another 50k
// to be entered as desk profit out of loki account as profit sharing — loki
// INR should decrease and desk profit to be increased." Loki is the desk's
// sub-broker on a 50/50 arrangement. The cash half was paid and lowered what
// the desk holds of his; the desk's half must lower his balance by the same
// amount with NO cash moving, and raise profit.
//
// The books do it as LEDGER-STANDARD §2.7's clawback: the sub-broker cost
// account → the party's balance (bff routes/expenses.ts /profit-share,
// numscript/profit_share.num). The party's balance falls by the amount; the
// sub-broker cost line falls by the same, so profit rises because a cost
// fell — no income is invented from a reduced liability.
//
// SIGN LAW, the same one every box keeps: negative = the desk holds theirs.
// The share comes OFF what the desk holds, so the balance moves toward square;
// the desk cannot take more than it holds, and the BFF refuses in words when
// it would (the same sentence sits under the amount here, before the tap).
//
// This file owns the form only. RecordMoney owns the mode selector and the
// page header and renders this body, the same way it renders RecordExpense
// and RecordBetweenParties. Nothing posts without the operator's tap.

import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, refreshMoneyViews } from '../lib/api';
import { tg } from '../lib/telegram';
import { ASSETS, makeReference } from '../lib/workflow';
import { amountOk, amountProblem } from '../lib/amount';
import { errorWords, isSentinel, nothingLanded, readWords } from '../lib/errors';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { rosterCut, usePartySearch } from '../lib/roster-cut';
import { AssetPicker } from '../components/AssetPicker';
import { AsOfChips } from '../components/AsOfChips';
import { Field, FieldRow } from '../components/Field';
import { BoxLines } from '../components/BoxLines';
import { ErrorLine } from '../components/ErrorLine';
import { StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { MoreDetails } from '../components/MoreDetails';
import {
  partyName, partySubtitle, isStale, needsNameReview, rankForQuery, foldAsset, partyBoxWords, partyBalanceOf,
} from '../lib/naming';
import { displayAsset, fmtAmount, fmtDay, dubaiWallToIso, backdateProblem } from '../lib/format';
import { readState } from '../lib/read-state';

/** The assets on offer, FOLDED: a party's balance speaks the fold (USDT, never
 *  USDT·TRC — no wallet is named here), so the chips speak it too. */
// The party's box folds the dollar stables into one USDT line (the 25 Aug
// ruling), so the share is taken in that line's own name — offering USDC
// would read "holds no USDC" against a box that holds it as USDT.
const SHARE_ASSETS: string[] = [...new Set(ASSETS.map((a) => foldAsset(a)))].filter((a) => a !== 'USDC');

/** THE RULE, before the tap: the share can never exceed what the desk holds of the party's balance in this asset. The BFF
 *  refuses the same way; this is the same refusal, earlier. At most ten words whatever the figure — the party is the
 *  field above it, and the figure and its unit are one value. */
export function shareProblem(held: number, amountN: number, balanceNext: number, asset: string): string | null {
  if (!(amountN > 0) || !(balanceNext > 0)) return null;
  const unit = displayAsset(asset);
  return held > 0
    ? `You hold ${fmtAmount(held, asset)}\u00a0${unit} — the share can't exceed it.`
    : `No ${unit} held — no share to take.`;
}

/** What the last posting said — read from what was POSTED, never from the live fields. */
export interface Posted { reference: string; asOf: string | null; deduped: boolean; amount: string; asset: string; name: string }

/** The answer to a share: a headline (design system §3: one line, at most ten words) and, under it in muted type, whose
 *  balance it came off. A deduped answer says so instead: the ledger already held this reference. */
export function PostedLine({ posted }: { posted: Posted }) {
  if (posted.deduped) {
    return (
      <StateLine tone="warn" action={<Link to="/movements" className="btn-link text-meta">Money movements →</Link>}>
        Already recorded as <span className="font-mono">{posted.reference}</span> — nothing changed; check its date.
      </StateLine>
    );
  }
  return (
    <div className="space-y-0.5">
      <StateLine tone="good" action={<CopyRef value={posted.reference} />}>
        ✓ Desk share recorded: <span className="whitespace-nowrap">{fmtAmount(posted.amount, posted.asset)} {displayAsset(posted.asset)}</span>
        {posted.asOf ? ` as of ${fmtDay(posted.asOf)}` : ''}
      </StateLine>
      <p className="text-meta text-tg-hint">off {posted.name}</p>
    </div>
  );
}

export function RecordProfitShare() {
  const [slug, setSlug]     = useState('');
  const [asset, setAsset]   = useState<string>('INR');
  const [amount, setAmount] = useState('');
  const [notes, setNotes]   = useState('');

  // ── WHEN DID THIS HAPPEN? — the same field as every other posting (5 Sept
  // 2026 door). Empty means now. Dubai wall-clock, read through lib/format.ts.
  const [when, setWhen] = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  // ── the party — the same picker as the Party mode, the same exclusions ────
  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: slug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q, with_balances: true }),
    enabled: slug.length > 0,
  });
  const rows = parties.data?.parties ?? [];
  // WHAT THE LIST IS WORTH, said in ONE place — `readState` (lib/read-state.ts; design system Appendix B). Unknown is never what it last said
  // and never a zero: an error (a failed REFRESH keeps its old rows beside it), a read that has not answered, or a list the BFF flags
  // `balances_read: false` (a ledger list it could not reach — the empty balances are not "nothing held"). Every line drawn from a figure
  // below takes the balances' answer: the picker's box words, the box line, the refusal, the read-back, and the button.
  const lookup = readState(parties);
  const listBalances = readState(parties, { balances: true });
  const balancesRead = listBalances === 'known';
  const partyOptions: ComboboxOption[] = useMemo(() =>
    // OWN ACCOUNTS ARE NOT PARTIES HERE: a profit share comes off a party's
    // balance, and a desk account has none.
    rankForQuery(rows.filter((p) => p.party_type !== 'own_account'), slug).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: [partySubtitle(p, { compact: true }), balancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [parties.data, balancesRead, slug]);   // eslint-disable-line react-hooks/exhaustive-deps
  const selected = rows.find((p) => p.slug === slug && p.party_type !== 'own_account') ?? null;
  const name = selected ? partyName(selected) : slug;

  // ── WHAT THE DESK HOLDS, before and after ─────────────────────────────────
  // The list row carries balances FOLDED (USDT), and the chips speak the fold,
  // so the key is the chip; foldAsset() is belt and braces.
  const amountN     = Number(amount) || 0;
  const amountWords = amountProblem(amount);
  const balanceNow: number | null = selected && balancesRead ? partyBalanceOf(selected.balances, asset) : null;   // null = unknown
  const held        = balanceNow !== null && balanceNow < 0 ? -balanceNow : 0;      // what the desk holds of theirs
  const balanceNext = balanceNow !== null ? balanceNow + amountN : null;            // the share comes OFF the box
  // THE RULE, before the tap (shareProblem above): the BFF refuses the same way; this is the same refusal, earlier. With the
  // figure unknown there is nothing to refuse FROM — and nothing to approve from either: the rule is keyed to the figure, so it
  // stays ON (`unchecked`) until a read confirms it. Silencing the refusal must never be what turns the button on.
  const shareWords = balanceNow !== null && balanceNext !== null ? shareProblem(held, amountN, balanceNext, asset) : null;
  const unchecked = !!selected && balanceNow === null;

  // ── submit ────────────────────────────────────────────────────────────────
  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY — minted
  // once per entry, held in a ref, cleared on success, retired when the day it
  // carries would change and the books provably hold nothing under it (the
  // same rule as Record money's refRef).
  const refRef = useRef<string | null>(null);
  const [posted, setPosted] = useState<Posted | null>(null);

  const post = async () => {
    refRef.current ??= makeReference('PSH', whenIso || undefined);
    const reference = refRef.current;
    const data = await api.profitShare({
      entity:       'dubaiotc',
      party_slug:   slug,
      amount:       { asset, amount: amountN },
      reference,
      notes:        notes.trim() || undefined,
      effective_at: whenIso || undefined,
    });
    return { data, reference, asOf: whenIso || null, amount, asset, name };
  };
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: post,
    onSuccess: ({ data, reference, asOf, amount, asset, name }) => {
      refRef.current = null;
      // The box on screen must show the share taken (reviewer, 17 Sept 2026).
      refreshMoneyViews(qc);
      setPosted({ reference: data.reference ?? reference, asOf, deduped: !!data.deduped, amount, asset, name });
      // The figures leave with the entry they were typed for; the party stays.
      setWhen(''); setAmount(''); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const retireReference = () => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) refRef.current = null;
  };

  const canPost = !submit.isPending && !!selected && !unchecked && amountOk(amount) && !shareWords && !whenWords;

  return (
    <div className="space-y-3 lg:space-y-4">
      <FieldRow>
        <Field label="Party">
          <Combobox
            value={slug}
            onChange={setSlug}
            options={partyOptions}
            more={rosterCut(parties.data)}
            loading={slug.length > 0 && lookup === 'waiting'}
            placeholder="Search party"
            emptyHint={slug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
          />
          {/* What the desk holds, in the standard words, before anything is
              typed — and where the balance lands once the amount is in. */}
          {balanceNow !== null && balanceNext !== null && (
            <BoxLines className="mt-1" asset={asset} before={balanceNow} after={amountN > 0 ? balanceNext : undefined} />
          )}
        </Field>
        <Field label={`Amount in ${displayAsset(asset)}`}>
          <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
          <ErrorLine>{shareWords}</ErrorLine>
        </Field>
      </FieldRow>
      {listBalances === 'failed' && (
        <StateLine tone="warn" more={lookup === 'failed' ? readWords(parties.error) : undefined} action={<button type="button" className="btn-link text-meta" onClick={() => void parties.refetch()}>Retry</button>}>
          {lookup === 'failed' ? 'Couldn’t read parties — try again.' : 'Balances not read — try again.'}
        </StateLine>
      )}

      <FieldRow>
        <Field label="Asset">
          <AssetPicker value={asset} onChange={setAsset} options={SHARE_ASSETS} />
        </Field>
        {/* AS OF — the chip row every entry screen carries: Now is an
            explicit choice, the picker appears only when asked for. */}
        <AsOfChips
          value={when}
          // The reference carries the day — a new day, a new reference,
          // unless the old one may already be in the ledger (retireReference).
          onChange={(w) => { setWhen(w); retireReference(); }}
          disabled={submit.isPending}
        />
      </FieldRow>

      <MoreDetails label="More">
        <Field label="Notes">
          <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
        </Field>
      </MoreDetails>

      {/* The read-back, only once everything is filled: what comes off the cost
          line. Where the balance lands is the box line under the party (before
          → after) — said once, there (21 Oct 2026 audit: nothing said twice). One
          muted line; on the phone it reads at the card size with the card
          leading (19 Sept 2026 phone pass); lg+ keeps its small line. */}
      {canPost && (
        <p className="text-meta max-lg:text-body max-lg:leading-snug text-tg-hint">
          <span className="whitespace-nowrap">{fmtAmount(amountN, asset)} {displayAsset(asset)}</span> comes off the sub-broker cost.
        </p>
      )}

      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={!canPost}
          onClick={() => { if (canPost) submit.mutate(); }}
        >
          {submit.isPending ? 'Booking…'
            // The only thing between the operator and the button is a balance nobody could read: say so on the button.
            : unchecked && amountOk(amount) && !whenWords ? 'Balance not read'
            : `Record profit share${whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : ''}`}
        </button>
      </div>

      {/* The success line reads what was POSTED, never the live fields. A
          deduped answer says so instead: the ledger already held this
          reference, and this request's amount and date are not what landed. */}
      {submit.isSuccess && posted && <PostedLine posted={posted} />}
      {submit.isError && !isSentinel(submit.error) && (
        <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>
      )}
    </div>
  );
}
